/**
 * BlockEditor
 *
 * Top-level editor component. Import this and render it wherever you need the
 * block-first editing experience.
 *
 * Usage:
 *   <BlockEditor
 *     initialDoc={doc}        // normalized doc object (or undefined for empty)
 *     title="My content"
 *     onSave={(doc) => …}     // receives the final doc on save
 *     onChange={(doc) => …}   // optional live doc updates on every edit
 *     onClose={() => …}       // called on cancel
 *   />
 *
 * The editor is fully self-contained; it owns its own state via
 * EditorStoreProvider. Pass `initialDoc` to pre-populate it.
 */
import React, { useCallback, useEffect } from 'react';
import { EditorStoreProvider, useDoc } from './store/editorStore.jsx';
import { Shell } from './ui/Shell.jsx';
import './blocks/index.js'; // registers all block types

function EditorInner({ title, onSave, onClose, onChange }) {
  const doc = useDoc();

  useEffect(() => {
    onChange && onChange(doc);
  }, [doc, onChange]);

  const handleSave = useCallback(() => {
    onSave && onSave(doc);
  }, [doc, onSave]);

  return <Shell title={title} onSave={handleSave} onClose={onClose} />;
}

export function BlockEditor({ initialDoc, title, onSave, onClose, onChange }) {
  return (
    <EditorStoreProvider initialDoc={initialDoc}>
      <EditorInner title={title} onSave={onSave} onClose={onClose} onChange={onChange} />
    </EditorStoreProvider>
  );
}
